import React, { useCallback, useEffect, useRef } from "react";
import Snackbar from "@material-ui/core/Snackbar";
import IconButton from "@material-ui/core/IconButton";
// ICONS
import CloseRoundedIcon from "@material-ui/icons/CloseRounded";
import CheckRoundedIcon from "@material-ui/icons/CheckRounded";
import PriorityHighRoundedIcon from "@material-ui/icons/PriorityHighRounded";
import InfoOutlinedIcon from "@material-ui/icons/InfoOutlined";
// HELPERS
import { TOAST_VARIANTS, TOAST_DURATION, resolveToastVariant, resolveToastTitle } from "Helpers/toastVariant";
// STYLES
import { SnackbarContentWrapper } from "./Toast.style";

const VARIANT_ICONS = {
	[TOAST_VARIANTS.SUCCESS]: CheckRoundedIcon,
	[TOAST_VARIANTS.ERROR]: CloseRoundedIcon,
	[TOAST_VARIANTS.WARNING]: PriorityHighRoundedIcon,
	[TOAST_VARIANTS.INFO]: InfoOutlinedIcon,
};

/**
 * In-game event banner.
 *
 * Props:
 *  - open        {boolean}
 *  - message     {string}
 *  - variant     {string} optional — success | error | warning | info.
 *                          Inferred from the message when omitted.
 *  - title       {string} optional — overrides the auto headline.
 *  - duration    {number} optional — ms on screen (default TOAST_DURATION).
 *  - handleClose {func}
 */
const Toast = (props) => {
	const { open, message, variant, title, duration, handleClose } = props;

	const resolvedVariant = resolveToastVariant(message, variant);
	const resolvedTitle = title || resolveToastTitle(resolvedVariant, message);
	const resolvedDuration = duration || TOAST_DURATION;
	const VariantIcon = VARIANT_ICONS[resolvedVariant] || InfoOutlinedIcon;

	const closeRef = useRef(handleClose);
	closeRef.current = handleClose;

	const requestClose = useCallback(() => {
		if (typeof closeRef.current === "function") closeRef.current();
	}, []);

	// Own timer — MUI's autoHideDuration pauses on hover / window blur, so the
	// progress bar can finish while the banner stays on screen.
	useEffect(() => {
		if (!open) return undefined;

		const timerId = window.setTimeout(requestClose, resolvedDuration);
		return () => window.clearTimeout(timerId);
	}, [open, resolvedDuration, requestClose]);

	const handleProgressEnd = useCallback(
		(event) => {
			if (event.target !== event.currentTarget) return;
			requestClose();
		},
		[requestClose]
	);

	const handleSnackbarClose = useCallback(
		(_event, reason) => {
			if (reason === "clickaway") return;
			requestClose();
		},
		[requestClose]
	);

	return (
		<Snackbar
			anchorOrigin={{ vertical: "top", horizontal: "center" }}
			classes={{ root: "snackbar-root" }}
			autoHideDuration={null}
			disableWindowBlurListener
			open={open}
			onClose={handleSnackbarClose}
			style={{ zIndex: "999999" }}
		>
			<SnackbarContentWrapper
				className={`event-banner toast-${resolvedVariant}`}
				aria-describedby="client-snackbar"
				classes={{ message: "notification-message", action: "notification-action" }}
				action={null}
				message={
					<div className="toast-card" role="status" aria-live="polite">
						<span className="glow-wash" />
						<span className="accent-rail" />

						<div className="icon-badge">
							<VariantIcon className="badge-icon" />
						</div>

						<div className="text-block">
							<p className="toast-title">{resolvedTitle}</p>
							<p className="toast-message">{message}</p>
						</div>

						<IconButton
							size="small"
							className="close-btn"
							aria-label="close"
							onClick={(event) => {
								event.stopPropagation();
								requestClose();
							}}
						>
							<CloseRoundedIcon className="close-icon" />
						</IconButton>

						<span className="progress-bar" style={{ animationDuration: `${resolvedDuration}ms` }} onAnimationEnd={handleProgressEnd} />
					</div>
				}
			/>
		</Snackbar>
	);
};

export default Toast;
